import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

// Heart of the Black Hills (near Hill City), not Rapid City on the eastern edge.
const DEFAULT_CENTER = { lat: 43.9319, lng: -103.5735 }
const DEFAULT_ZOOM = 8
const MARKER_BACKGROUND = "#e0512f"
const MARKER_BORDER = "#a63c1d"

// Read-only map that plots the itinerary's stops as numbered markers and
// re-renders when the itinerary list dispatches `trip-itinerary-sort:changed`.
// Day / trip map icons filter markers; a spinner covers the map until idle.
export default class extends Controller {
  static values = {
    apiKey: String,
    mapId: String,
    points: Array
  }

  static targets = ["canvas", "empty", "dayFilter", "loading"]

  connect() {
    if (!this.apiKeyValue) return

    this.markers = []
    this.filterDayId = null
    this.loadMap()
  }

  disconnect() {
    this.clearMarkers()
    if (this.map && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.map)
    }
  }

  async loadMap() {
    this.showLoading()

    try {
      if (!window.google?.maps && !document.querySelector('script[src*="maps.googleapis.com"]')) {
        setOptions({ key: this.apiKeyValue })
      }

      const { Map } = await importLibrary("maps")
      const { AdvancedMarkerElement, PinElement } = await importLibrary("marker")

      this.MarkerClass = AdvancedMarkerElement
      this.PinClass = PinElement

      this.map = new Map(this.canvasTarget, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapId: this.mapIdValue,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true
      })

      await this.renderAndWait(this.pointsValue || [])
    } finally {
      this.hideLoading()
    }
  }

  // Toggle map markers to one day; click again to show the full itinerary.
  async filterDay(event) {
    event.preventDefault()
    const dayId = event.params.dayId?.toString()
    if (!dayId) return

    this.filterDayId = this.filterDayId === dayId ? null : dayId
    this.syncFilterButtons()
    await this.refreshWithLoading(this.pointsForDay(this.filterDayId))
  }

  // Clear any day filter and show every stop (trip header map icon).
  async showAll(event) {
    event?.preventDefault?.()
    this.filterDayId = null
    this.syncFilterButtons()
    await this.refreshWithLoading(this.pointsForDay(null))
  }

  async pointsChanged() {
    await this.renderAndWait(this.pointsForDay(this.filterDayId))
  }

  async refreshWithLoading(points) {
    if (!this.map || !this.MarkerClass) return

    this.showLoading()
    try {
      await this.renderAndWait(points)
    } finally {
      this.hideLoading()
    }
  }

  pointsForDay(dayId) {
    const rows = dayId
      ? this.element.querySelectorAll(`[data-trip-day-id="${dayId}"] [data-trip-thing-id]`)
      : this.element.querySelectorAll("[data-trip-day-id] [data-trip-thing-id]")

    const points = []
    rows.forEach((row) => {
      const lat = parseFloat(row.dataset.lat)
      const lng = parseFloat(row.dataset.lng)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        points.push({ lat, lng, name: row.dataset.name })
      }
    })
    return points
  }

  syncFilterButtons() {
    this.dayFilterTargets.forEach((button) => {
      const dayId = button.dataset.tripMapDayIdParam
      const active = this.filterDayId !== null && dayId === this.filterDayId
      const label = active
        ? "Show all days on the map"
        : "Show only this day's itinerary on the map"

      button.setAttribute("aria-pressed", String(active))
      button.setAttribute("aria-label", label)
      button.classList.toggle("text-(--brand-orange)", active)
      button.classList.toggle("text-gray-400", !active)

      const hint = button
        .closest("[data-controller~='anchored-popup']")
        ?.querySelector("[data-trip-map-hint]")
      if (hint) hint.textContent = label
    })
  }

  renderAndWait(points) {
    if (!this.map || !this.MarkerClass) return Promise.resolve()

    this.clearMarkers()

    const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", valid.length > 0)

    if (valid.length === 0) {
      this.map.setCenter(DEFAULT_CENTER)
      this.map.setZoom(DEFAULT_ZOOM)
      return this.waitForIdle()
    }

    const bounds = new window.google.maps.LatLngBounds()

    this.markers = valid.map((point, index) => {
      const position = { lat: point.lat, lng: point.lng }
      bounds.extend(position)

      const pin = new this.PinClass({
        glyph: String(index + 1),
        glyphColor: "#ffffff",
        background: MARKER_BACKGROUND,
        borderColor: MARKER_BORDER
      })

      return new this.MarkerClass({
        position,
        map: this.map,
        title: point.name || "",
        content: pin.element
      })
    })

    if (valid.length === 1) {
      this.map.setCenter(valid[0])
      this.map.setZoom(14)
    } else {
      this.map.fitBounds(bounds, 48)
    }

    return this.waitForIdle()
  }

  // Resolves when the map finishes a camera update, or shortly after if the
  // viewport does not change (e.g. filtering a one-day trip to that same day).
  // Google Maps does not emit idle again when fitBounds/setCenter are no-ops,
  // so waiting only on idle leaves the loading spinner stuck forever.
  waitForIdle() {
    return new Promise((resolve) => {
      if (!this.map || !window.google?.maps?.event) {
        resolve()
        return
      }

      let settled = false
      let listener = null
      let timeoutId = null

      const finish = () => {
        if (settled) return
        settled = true
        if (listener) window.google.maps.event.removeListener(listener)
        if (timeoutId) clearTimeout(timeoutId)
        resolve()
      }

      listener = window.google.maps.event.addListenerOnce(this.map, "idle", finish)
      timeoutId = setTimeout(finish, 400)
    })
  }

  showLoading() {
    if (!this.hasLoadingTarget) return

    this.loadingTarget.classList.remove("hidden")
    this.loadingTarget.classList.add("flex")
    this.loadingTarget.setAttribute("aria-hidden", "false")
  }

  hideLoading() {
    if (!this.hasLoadingTarget) return

    this.loadingTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("flex")
    this.loadingTarget.setAttribute("aria-hidden", "true")
  }

  clearMarkers() {
    (this.markers || []).forEach((marker) => { marker.map = null })
    this.markers = []
  }
}
